'use client';
import React, { useState, useRef, useEffect } from 'react';
import AppLayout from '@/components/AppLayout';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  subject?: string;
}

const AI_MODES = [
  { id: 'tutor', label: 'AI Tutor', icon: '🎓', description: 'Get personalized explanations for any topic' },
  { id: 'homework', label: 'Homework Help', icon: '📝', description: 'Step-by-step homework assistance' },
  { id: 'quiz', label: 'Quiz Generator', icon: '❓', description: 'Generate practice quizzes on any subject' },
  { id: 'career', label: 'Career Advisor', icon: '🚀', description: 'Explore career pathways and guidance' },
  { id: 'study', label: 'Study Planner', icon: '📅', description: 'Create personalized study schedules' },
  { id: 'summarize', label: 'Lesson Summarizer', icon: '📋', description: 'Summarize lessons and key concepts' },
  { id: 'translate', label: 'AI Translator', icon: '🌐', description: 'Translate content to 9 languages' },
  { id: 'voice', label: 'Voice Tutor', icon: '🎙️', description: 'Interactive voice-based learning' },
];

const QUICK_PROMPTS = [
  { label: 'Explain photosynthesis', subject: 'Biology' },
  { label: 'Help me with algebra', subject: 'Mathematics' },
  { label: 'What is Tajweed?', subject: 'Quran' },
  { label: 'Career in medicine', subject: 'Career' },
  { label: 'Python for loops', subject: 'Computer Science' },
  { label: 'Islamic finance basics', subject: 'Islamic Finance' },
  { label: 'Study plan for exams', subject: 'Study Skills' },
  { label: 'Explain the Seerah', subject: 'Islamic Studies' },
];

const INITIAL_MESSAGES: Message[] = [
  {
    id: 'welcome',
    role: 'assistant',
    content: "السلام عليكم! I'm your ILM Hub AI Tutor. I can help you with any subject — from Mathematics and Science to Quran, Arabic, and Islamic Studies. I support 9 languages and can adapt to your learning level.\n\nWhat would you like to learn today? 📚",
    timestamp: new Date(),
  },
];

const SUBJECT_RESPONSES: Record<string, string> = {
  photosynthesis: "**Photosynthesis** is the process by which plants convert sunlight, water, and carbon dioxide into glucose and oxygen.\n\n**The equation:**\n6CO₂ + 6H₂O + light energy → C₆H₁₂O₆ + 6O₂\n\n**Key stages:**\n1. **Light-dependent reactions** (in thylakoids) — capture light energy, split water, produce ATP and NADPH\n2. **Calvin Cycle** (in stroma) — uses ATP and NADPH to fix CO₂ into glucose\n\n**Islamic connection:** Allah ﷻ says: *'And it is He who sends down rain from the sky, and We produce thereby the growth of all things.'* (Al-An'am 6:99)\n\nWould you like a quiz on this topic? 🌿",
  algebra: "**Solving Linear Equations** — let's work through it step by step!\n\n**Example:** Solve 3x + 7 = 22\n\n**Step 1:** Subtract 7 from both sides\n3x + 7 - 7 = 22 - 7\n3x = 15\n\n**Step 2:** Divide both sides by 3\n3x ÷ 3 = 15 ÷ 3\n**x = 5**\n\n**Check:** 3(5) + 7 = 15 + 7 = 22 ✅\n\nWant me to generate practice problems for you? 🔢",
  tajweed: "**Tajweed** (تجويد) means 'to make better' or 'to improve.' It refers to the rules governing the correct pronunciation of the Quran.\n\n**Key Tajweed rules:**\n\n1. **Noon Saakinah & Tanween** — 4 rules: Ikhfa, Idgham, Iqlab, Izhar\n2. **Meem Saakinah** — 3 rules: Ikhfa Shafawi, Idgham Shafawi, Izhar Shafawi\n3. **Madd (elongation)** — Natural Madd (2 counts), Obligatory Madd (4-5 counts)\n4. **Qalqalah** — Echoing sound on letters: ق ط ب ج د\n\nThe Prophet ﷺ said: *'The best of you are those who learn the Quran and teach it.'* (Bukhari)\n\nShall I create a Tajweed quiz for you? 📿",
  default: "That's a great question! Let me help you understand this topic thoroughly.\n\nI can explain concepts at any level — from beginner to advanced. I can also:\n- Generate practice quizzes 📝\n- Create study plans 📅\n- Provide real-world examples 🌍\n- Connect topics to Islamic values 🌿\n\nCould you tell me more about what specifically you'd like to learn? The more detail you give me, the better I can help! 🎓",
};

function getAIResponse(message: string): string {
  const lower = message.toLowerCase();
  if (lower.includes('photosynthesis')) return SUBJECT_RESPONSES.photosynthesis;
  if (lower.includes('algebra') || lower.includes('equation')) return SUBJECT_RESPONSES.algebra;
  if (lower.includes('tajweed') || lower.includes('quran')) return SUBJECT_RESPONSES.tajweed;
  return SUBJECT_RESPONSES.default;
}

export default function AITutorPage() {
  const [activeMode, setActiveMode] = useState('tutor');
  const [messages, setMessages] = useState<Message[]>(INITIAL_MESSAGES);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [selectedLang, setSelectedLang] = useState('en');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async (text?: string) => {
    const messageText = text || input.trim();
    if (!messageText) return;

    const userMsg: Message = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: messageText,
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setIsTyping(true);

    await new Promise(r => setTimeout(r, 1200));

    const aiMsg: Message = {
      id: `ai-${Date.now()}`,
      role: 'assistant',
      content: getAIResponse(messageText),
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, aiMsg]);
    setIsTyping(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <AppLayout activeRoute="/ai-tutor">
      <div className="space-y-6 animate-fade-in">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-800 text-foreground">AI Learning Suite</h1>
            <p className="text-sm text-muted-foreground mt-1">8 AI-powered tools for personalized learning in 9 languages</p>
          </div>
          <select
            value={selectedLang}
            onChange={e => setSelectedLang(e.target.value)}
            className="text-xs border border-border rounded-xl px-3 py-2 bg-card text-foreground focus:outline-none"
          >
            {[['en','🇬🇧 English'],['ar','🇸🇦 العربية'],['fr','🇫🇷 Français'],['ha','🇳🇬 Hausa'],['sw','🇰🇪 Swahili'],['ur','🇵🇰 اردو'],['es','🇪🇸 Español'],['pt','🇧🇷 Português'],['yo','🇳🇬 Yorùbá']].map(([code, label]) => (
              <option key={code} value={code}>{label}</option>
            ))}
          </select>
        </div>

        {/* AI Mode Selector */}
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2">
          {AI_MODES.map(mode => (
            <button
              key={mode.id}
              onClick={() => setActiveMode(mode.id)}
              className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border text-center transition-all ${activeMode === mode.id ? 'bg-primary/10 border-primary text-primary' : 'bg-card border-border text-foreground hover:border-primary/40'}`}
            >
              <span className="text-2xl">{mode.icon}</span>
              <span className="text-[10px] font-700 leading-tight">{mode.label}</span>
            </button>
          ))}
        </div>

        {/* Active Mode Description */}
        <div className="bg-gradient-to-r from-primary/5 to-teal/5 border border-primary/20 rounded-xl px-4 py-3">
          <p className="text-sm text-foreground">
            <span className="font-700">{AI_MODES.find(m => m.id === activeMode)?.icon} {AI_MODES.find(m => m.id === activeMode)?.label}:</span>{' '}
            {AI_MODES.find(m => m.id === activeMode)?.description}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Chat Interface */}
          <div className="lg:col-span-3 flex flex-col bg-card rounded-2xl border border-border overflow-hidden" style={{ height: '520px' }}>
            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.map(msg => (
                <div key={msg.id} className={`flex gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm flex-shrink-0 ${msg.role === 'assistant' ? 'bg-gradient-to-br from-primary to-teal-600 text-white' : 'bg-gradient-to-br from-amber-500 to-orange-600 text-white'}`}>
                    {msg.role === 'assistant' ? '🤖' : 'Y'}
                  </div>
                  <div className={`max-w-[80%] rounded-2xl px-4 py-3 ${msg.role === 'assistant' ? 'bg-muted text-foreground' : 'bg-primary text-white'}`}>
                    <div className="text-sm whitespace-pre-wrap leading-relaxed">
                      {msg.content.split('\n').map((line, i) => {
                        if (line.startsWith('**') && line.endsWith('**')) {
                          return <p key={i} className="font-700 mt-1">{line.replace(/\*\*/g, '')}</p>;
                        }
                        if (line.startsWith('**')) {
                          return <p key={i} className="mt-1" dangerouslySetInnerHTML={{ __html: line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }} />;
                        }
                        return <p key={i} className={line === '' ? 'mt-1' : ''}>{line}</p>;
                      })}
                    </div>
                  </div>
                </div>
              ))}
              {isTyping && (
                <div className="flex gap-3">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-teal-600 flex items-center justify-center text-sm">🤖</div>
                  <div className="bg-muted rounded-2xl px-4 py-3">
                    <div className="flex gap-1">
                      <span className="w-2 h-2 bg-muted-foreground/40 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="w-2 h-2 bg-muted-foreground/40 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                      <span className="w-2 h-2 bg-muted-foreground/40 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="border-t border-border p-4">
              <div className="flex gap-3">
                <textarea
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Ask me anything — any subject, any level, any language..."
                  className="flex-1 resize-none bg-muted rounded-xl px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 min-h-[44px] max-h-[120px]"
                  rows={1}
                />
                <button
                  onClick={() => sendMessage()}
                  disabled={!input.trim() || isTyping}
                  className="w-11 h-11 bg-primary text-white rounded-xl flex items-center justify-center hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed transition-all flex-shrink-0"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                  </svg>
                </button>
              </div>
            </div>
          </div>

          {/* Quick Prompts */}
          <div className="space-y-4">
            <div className="bg-card rounded-2xl border border-border p-4">
              <h3 className="font-700 text-sm text-foreground mb-3">Quick Start</h3>
              <div className="space-y-2">
                {QUICK_PROMPTS.map((prompt, i) => (
                  <button
                    key={i}
                    onClick={() => sendMessage(prompt.label)}
                    className="w-full text-left px-3 py-2.5 rounded-xl bg-muted hover:bg-primary/10 hover:text-primary text-sm text-foreground transition-all group"
                  >
                    <p className="font-600 text-xs">{prompt.label}</p>
                    <p className="text-[10px] text-muted-foreground group-hover:text-primary/70">{prompt.subject}</p>
                  </button>
                ))}
              </div>
            </div>

            <div className="bg-gradient-to-br from-primary/10 to-teal/10 rounded-2xl border border-primary/20 p-4">
              <h3 className="font-700 text-sm text-foreground mb-2">🌐 9 Languages</h3>
              <p className="text-xs text-muted-foreground">Ask questions in English, Arabic, French, Hausa, Swahili, Urdu, Spanish, Portuguese, or Yoruba.</p>
              <div className="flex flex-wrap gap-1 mt-2">
                {['🇬🇧', '🇸🇦', '🇫🇷', '🇳🇬', '🇰🇪', '🇵🇰', '🇪🇸', '🇧🇷'].map((flag, i) => (
                  <span key={i} className="text-lg">{flag}</span>
                ))}
              </div>
            </div>

            <div className="bg-card rounded-2xl border border-border p-4">
              <h3 className="font-700 text-sm text-foreground mb-2">📊 Session Stats</h3>
              <div className="space-y-2">
                <div className="flex justify-between text-xs">
                  <span className="text-muted-foreground">Questions asked</span>
                  <span className="font-700 text-foreground">{messages.filter(m => m.role === 'user').length}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-muted-foreground">Topics covered</span>
                  <span className="font-700 text-foreground">{Math.max(1, messages.filter(m => m.role === 'user').length)}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-muted-foreground">XP earned</span>
                  <span className="font-700 text-amber-600">+{messages.filter(m => m.role === 'user').length * 15} XP</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
